import { Call, Device } from '@twilio/voice-sdk';
import { hasDeviceIdAsString } from '../sic-lib/utils/functionalFilters';

export type TwilioCallStatus =
    | 'pending'
    | 'connecting'
    | 'ringing'
    | 'open'
    | 'reconnecting'
    | 'closed';

/*
    reference twilio device:
    https://www.twilio.com/docs/voice/client/javascript/device
*/

export interface SerializedTwilioDevice {
    /*
        whether or not Device.setup has been called
        */
    /*
    status will be one of:
        > 'ready': device is connected to twilio with valid access token => registered
        > 'offline: device is either not connected to Twilio or has an expired access token => unregistered
        > 'busy: device is connected to Twilio with an active connection (cannot connect to new calls)
 
        
 */
    state: 'unregistered' | 'registered' | 'registering' | 'destroyed';
    /*
        whether or not Device is in a busy.
    */
    isBusy: boolean;
    audioDevices: Omit<MediaDeviceInfo, 'toJSON'>[];
    audioInputDevice: MediaDeviceInfo | undefined;
}

/**
 * Map useful Twilio device instance properties into object that can be held within Redux
 * @param device
 */
export function serializeTwilioDevice(
    device: Device | undefined,
): SerializedTwilioDevice | undefined {
    if (!device) return undefined;
    return {
        state: device.state,
        isBusy: device.isBusy,
        audioDevices: device.audio
            ? Array.from(device.audio.availableInputDevices.values())
                  .filter(hasDeviceIdAsString)
                  .map(itm => ({
                      deviceId: itm.deviceId,
                      groupId: itm.groupId,
                      label: itm.label,
                      kind: itm.kind,
                  }))
            : [],
        audioInputDevice: device.audio?.inputDevice || undefined,
    };
}

/*
    reference twilio connection:
    https://www.twilio.com/docs/voice/client/javascript/connection
*/

export interface SerializedTwilioCall {
    direction: 'incoming' | 'outgoing';
    /*
        TIP: from testing it appears OUTGOING call events mostly use 'open' and 'closed' state, 
        ignoring itermediary steps of 'pending' or 'ringing'

        status will be one of:
            > 'pending': connection is INCOMMING but not yet established
            > 'connecting': connection has been accepted by the user
            > 'ringing': connection is OUTGOING; callee has not yet accepted the call
            > 'open': connection has been established; active phone call
            > 'reconnecting':The ICE connection was disconnected and a reconnection has been triggered.
            > 'closed': connection has been disconnected
     */
    status: TwilioCallStatus;
    parameters: {
        /* 
            custom parameters:
            -------------------
            from client: passed through on call to device.connect and received back in the connection object
            from server: 
        */
        contactId?: number;
        contactName?: string;
        from?: string;
    };
}

/**
 * Map useful Twilio connection instance properties into object that can be held within Redux
 * @param connection
 */
export function serializeTwilioCall(call: Call): SerializedTwilioCall {
    const contactId = call.customParameters.get('ContactId');
    const firstName = call.customParameters.get('FirstName') || '';
    const lastName = call.customParameters.get('LastName') || '';
    const contactName = (firstName + ' ' + lastName).trim();

    return {
        direction: call.direction === Call.CallDirection.Incoming ? 'incoming' : 'outgoing',
        status: call.status(),
        parameters: {
            contactId: contactId !== undefined ? parseInt(contactId) : undefined,
            contactName: contactName,
            from: call.parameters.From,
        },
    };
}

interface GetMediaConstraints {
    audio: boolean;
    /*
        video constraint can have many other options than just being selectable.
        in case video support is added in future releases, see:
        https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
     */
    video?: boolean;
}
/**
 * getMedia
 *
 * @description
 * Prompts the user to allow or block the use of microphons by the browser tab
 *
 * IMPORTANT: if a user chooses the 'Deny' or 'Block' option when prompted for permissions
 * this function cannot be re-run; it will return an error. User must clear site settings in
 * browser settings and refresh the page to be prompted again.
 */
export async function getMedia(constraints: GetMediaConstraints): Promise<boolean> {
    try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        /*
            Calling getUserMedia will start the media track selected.
            This is not desired as the user may get the impression the mic is in use.
            Therefore, we want to avoid having tracks started when they're not needed.
            We only wanted to get the input device list so we stop the tracks immediately.

            As described in example here: 
            https://www.twilio.com/docs/voice/sdks/javascript/best-practices#working-with-microphones-and-getusermedia
         */
        stream.getTracks().forEach(track => track.stop());
        return true;
    } catch (e: any) {
        console.log('[ERROR] - getMedia: ', e);
        return false;
    }
}

export function friendlyCallStatus(status: TwilioCallStatus | undefined) {
    if (!status) return 'Ready';

    switch (status) {
        case 'pending':
            return 'Inbound Call';
        case 'connecting':
            return 'Connecting';
        case 'ringing':
            return 'Ringing';
        case 'open':
            return 'Connected';
        case 'reconnecting':
            return 'Reconnecting';
        case 'closed':
            return 'Ended';
    }
}
